/**
 * Meekijken — the read-only look-along session an operator opens on a
 * customer's app for support. Thirty minutes, one tab, no writes.
 *
 * WHY sessionStorage, when auth.ts argues the exact opposite for the
 * operator's own token: this one has to be per TAB. The operator looks along
 * with a customer in one tab and keeps working on their own account in
 * another, and a module variable cannot tell those two tabs apart — the new
 * tab is a new module instance with nothing in it, and the hash that carries
 * the token is read exactly once. sessionStorage is the only storage the
 * browser scopes to a tab.
 *
 * The exception is deliberate and it is bounded: what sits here is a
 * thirty-minute token that cannot write anything (backend/auth/dependencies.py
 * refuses every non-safe method on it), not the operator's own 72-hour
 * credential. That one stays in memory where auth.ts put it, and nothing in
 * this module touches it.
 *
 * The token arrives in the URL FRAGMENT, never the query string: a fragment is
 * not sent to the server and so never lands in an access log. It is wiped from
 * the address bar the moment it has been read.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'priceq.meekijken';
const HASH_TOKEN = 'impersonate';
const HASH_NAME = 'klant';
const HASH_UNTIL = 'tot';

export interface LookAlong {
  token: string;
  /** The customer's name, for the band. Empty when we were not told it —
   *  the band then says "een klant" rather than rendering a gap. */
  tenantName: string;
  /** When the token stops working (ISO, straight out of the token itself, see
   *  `ImpersonationGrant`). `null` when we were not told: the band then shows
   *  no clock at all rather than a guessed one. */
  expiresAt: string | null;
}

const listeners = new Set<() => void>();

// `undefined` = not read from storage yet. Every mutation puts it back to
// undefined rather than to the new value, so storage stays the single source
// of truth and get() still hands out a stable object between mutations —
// which is what useSyncExternalStore needs to avoid re-rendering forever.
let cache: LookAlong | null | undefined;

function readStorage(): LookAlong | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LookAlong> | null;
    if (!parsed || typeof parsed.token !== 'string' || parsed.token.length === 0) return null;
    return {
      token: parsed.token,
      tenantName: typeof parsed.tenantName === 'string' ? parsed.tenantName : '',
      expiresAt: typeof parsed.expiresAt === 'string' ? parsed.expiresAt : null,
    };
  } catch {
    // Storage blocked (private mode), or a value that is not ours. Either way
    // there is no look-along, which is the safe answer.
    return null;
  }
}

function announce(): void {
  cache = undefined;
  for (const listener of listeners) listener();
}

export function get(): LookAlong | null {
  if (cache === undefined) cache = readStorage();
  return cache;
}

export function set(token: string, tenantName: string, expiresAt: string | null = null): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ token, tenantName, expiresAt }));
  } catch {
    // Nothing to do: without storage there is no look-along, and the caller
    // finds that out on the next get().
  }
  announce();
}

/** End the look-along. This is the WHOLE of ending one: the server is never
 *  told, because the only call that could tell it (`/api/v1/auth/logout`)
 *  carries no Authorization header and would revoke the operator's own
 *  session instead. The thirty-minute expiry is the real bound. */
export function clear(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // See set().
  }
  announce();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The fragment the second tab is opened with. */
export function hashFor(token: string, tenantName: string, expiresAt: string | null = null): string {
  const params = new URLSearchParams({ [HASH_TOKEN]: token, [HASH_NAME]: tenantName });
  if (expiresAt) params.set(HASH_UNTIL, expiresAt);
  return `#${params.toString()}`;
}

/**
 * Read a look-along out of the address bar and wipe it from there.
 *
 * Must run before main.tsx trades the refresh cookie for a token, and it is
 * called exactly once — after this the fragment is gone, so a reload cannot
 * replay it and nobody can copy the URL to a colleague.
 */
export function readFromHash(): LookAlong | null {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash.length === 0) return null;
    const params = new URLSearchParams(hash);
    const token = params.get(HASH_TOKEN);
    if (!token) return null;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return {
      token,
      tenantName: params.get(HASH_NAME) ?? '',
      expiresAt: params.get(HASH_UNTIL),
    };
  } catch {
    return null;
  }
}

/** React's view of the same store. Lives here rather than in hooks/ so the
 *  store and its only subscriber cannot drift apart. */
export function useLookAlong(): LookAlong | null {
  return useSyncExternalStore(subscribe, get, get);
}
